import {DealPayrollGrade, DealPayrollGradeCode} from 'app/models/deal-payroll.model';
import {dealPayrollGradeKey} from 'app/models/deal-payroll-presentation';
import {GrowthDashboardData, GrowthDecimal, GradePayrollComparison, GrowthPayrollReference, GrowthLearningPreview} from './grade-growth.model';
import {growthDecimalText, isGrowthDecimal, growthLearningPreviewView, GrowthLearningPreviewView} from './grade-growth-presentation';

const ZERO = 0;
const AVAILABLE = 'AVAILABLE';
const POLICY_GRADE_MISMATCH = 'POLICY_GRADE_MISMATCH';
const PLANFIX_DEAL = 'PLANFIX_DEAL';
const PAIR = 2;
const SPACE = ' ';
const EMPTY = '';
const GRADE_GROWTH_MONEY_CONTEXT_PENDING = 'GRADE_GROWTH_MONEY_CONTEXT_PENDING';
const FORBIDDEN = 'FORBIDDEN';
const GRADE_GROWTH_COMPARISON_DENIED = 'GRADE_GROWTH_COMPARISON_DENIED';
const NOT_CONFIGURED = 'NOT_CONFIGURED';
const GRADE_INDEPENDENT = 'GRADE_INDEPENDENT';
const GRADE_GROWTH_NO_DEAL = 'GRADE_GROWTH_NO_DEAL';
const GRADE_GROWTH_PAYROLL_POLICY_REQUIRED = 'GRADE_GROWTH_PAYROLL_POLICY_REQUIRED';
const GRADE_GROWTH_COMPARISON_GRADE_INDEPENDENT = 'GRADE_GROWTH_COMPARISON_GRADE_INDEPENDENT';
const GRADE_GROWTH_POLICY_GRADE_MISMATCH = 'GRADE_GROWTH_POLICY_GRADE_MISMATCH';
const NO_SNAPSHOT = 'NO_SNAPSHOT';
const GRADE_GROWTH_COMPARISON_NO_SNAPSHOT = 'GRADE_GROWTH_COMPARISON_NO_SNAPSHOT';
const GRADE_GROWTH_COMPARISON_SAME_WORK = 'GRADE_GROWTH_COMPARISON_SAME_WORK';
const GRADE_GROWTH_COMPARISON_UNAVAILABLE = 'GRADE_GROWTH_COMPARISON_UNAVAILABLE';
const DEFAULT_LABEL_HEIGHT = 100;
const LEADER_HEIGHT = 32;
const CHART_PADDING = 24;
const FOOTER_PADDING = 36;
const HORIZONTAL_MIN_WIDTH = 1000;
const UNIT = 1;
const LABEL_COLUMN_WIDTH = 120;
const COMPACT_LABEL_WIDTH = 80;
const VERTICAL_LABEL_SPACE = 122;
const LABEL_GAP = 16;
const VERTICAL_START = 44;
const MIN_VERTICAL_EXTENT = 480;
const HORIZONTAL_PADDING = 48;
const VERTICAL_AXIS = 94;
const HORIZONTAL_AXIS = 88;
const CURSOR_OFFSET = 22;
const COMMA = ',';
const CURSOR_RADIUS = 7;
const LABEL_AXIS_OFFSET = 28;
const LABEL_INSET = 8;

export interface GrowthMilestone {
  learningPreview?: GrowthLearningPreviewView;
  code: DealPayrollGradeCode;
  labelKey: string;
  threshold: number;
  thresholdText: string;
  current: boolean;
  next: boolean;
  moneyText: string;
  referenceText: string;
  x: number;
  y: number;
  labelX: number;
  labelY: number;
  leaderX: number;
  leaderY: number;
}
export interface GrowthChartLayout {
  width: number;
  height: number;
  vertical: boolean;
  labelWidth: number;
  maximum: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  cursorX: number;
  cursorY: number;
  cursorPoints: string;
  milestones: GrowthMilestone[];
  intervalStart?: GrowthMilestone;
  intervalEnd?: GrowthMilestone;
}

export function growthFinitePoints(value: GrowthDecimal): boolean {
  return isGrowthDecimal(value) && isFinite(Number(value)) && Number(value) >= ZERO;
}

export function growthCompatibleMoney(data: GrowthDashboardData): GradePayrollComparison {
  const money = data && data.money;
  const context = data && data.calculationContext;
  return money && context && money.userId === data.userId && money.calculationContextId === context.contextId
    ? money : null;
}

export function growthAvailableReference(data: GrowthDashboardData): GrowthPayrollReference {
  const reference = data && data.payrollReference;
  const money = data && data.money;
  if (!reference || reference.status !== AVAILABLE || reference.payrollCalculationMethod !== PLANFIX_DEAL
      || !reference.payrollCurrency) { return null; }
  if (money && (!growthCompatibleMoney(data) || money.status === FORBIDDEN
      || money.status === NOT_CONFIGURED || money.status === GRADE_INDEPENDENT)) { return null; }
  return reference;
}

export function growthMilestones(data: GrowthDashboardData, locale: string): GrowthMilestone[] {
  const evaluation = data.evaluation;
  const current = data.gradeCode ? data.gradeCode : evaluation && evaluation.growthGrade ? evaluation.growthGrade.code : null;
  const target = current && evaluation && evaluation.target ? evaluation.target.code : null;
  const money = growthCompatibleMoney(data);
  const monetary = money && (money.status === AVAILABLE || money.status === POLICY_GRADE_MISMATCH)
    && money.paymentMethod === PLANFIX_DEAL;
  const context = {current: current, target: target, money: monetary ? money : null,
    reference: growthAvailableReference(data), locale: locale, learningPreview: data.learningPreview};
  return (data.grades || []).filter(step => step && step.enabled !== false && growthFinitePoints(step.minPoints))
    .map(step => milestone(step, context))
    .sort((left, right) => left.threshold - right.threshold);
}

function milestone(step: DealPayrollGrade, context: {current: DealPayrollGradeCode; target: DealPayrollGradeCode;
    money: GradePayrollComparison; reference: GrowthPayrollReference; locale: string; learningPreview: GrowthLearningPreview}): GrowthMilestone {
  const row = context.money && (context.money.rows || []).filter(item => item && item.gradeCode === step.gradeCode)[ZERO];
  const amount = row && row.availability === AVAILABLE
    ? moneyText(row.estimatedSalary, row.currency, {locale: context.locale, fractionDigits: PAIR}) : EMPTY;
  const reference = context.reference && (context.reference.grades || [])
    .filter(item => item && item.enabled !== false && item.gradeCode === step.gradeCode)[ZERO];
  const referenceText = reference
    ? moneyText(reference.monthlySalary, context.reference.payrollCurrency, {locale: context.locale, fractionDigits: ZERO}) : EMPTY;
  return {learningPreview: growthLearningPreviewView(context.learningPreview, step.gradeCode),
    code: step.gradeCode, labelKey: dealPayrollGradeKey(step.gradeCode), threshold: Number(step.minPoints),
    thresholdText: growthDecimalText(step.minPoints), current: step.gradeCode === context.current,
    next: step.gradeCode === context.target, moneyText: amount, referenceText: referenceText,
    x: ZERO, y: ZERO, labelX: ZERO, labelY: ZERO, leaderX: ZERO, leaderY: ZERO};
}

function moneyText(amount: GrowthDecimal, currency: string, format: {locale: string; fractionDigits: number}): string {
  return isGrowthDecimal(amount) && isFinite(Number(amount)) && currency
    ? Number(amount).toLocaleString(format.locale,
      {minimumFractionDigits: format.fractionDigits, maximumFractionDigits: format.fractionDigits}) + SPACE + currency : EMPTY;
}

export function growthMoneyStatus(data: GrowthDashboardData): string {
  const money = growthCompatibleMoney(data);
  if (!money) { return GRADE_GROWTH_MONEY_CONTEXT_PENDING; }
  if (money.status === FORBIDDEN) { return GRADE_GROWTH_COMPARISON_DENIED; }
  if (money.status === GRADE_INDEPENDENT) { return GRADE_GROWTH_COMPARISON_GRADE_INDEPENDENT; }
  if (money.status === NOT_CONFIGURED) {
    return money.paymentMethod === PLANFIX_DEAL ? GRADE_GROWTH_PAYROLL_POLICY_REQUIRED : GRADE_GROWTH_NO_DEAL;
  }
  if (money.status === POLICY_GRADE_MISMATCH) { return GRADE_GROWTH_POLICY_GRADE_MISMATCH; }
  if (money.status === NO_SNAPSHOT) { return GRADE_GROWTH_COMPARISON_NO_SNAPSHOT; }
  if (money.status === AVAILABLE && money.paymentMethod === PLANFIX_DEAL) { return GRADE_GROWTH_COMPARISON_SAME_WORK; }
  return GRADE_GROWTH_COMPARISON_UNAVAILABLE;
}

// Only presentation geometry: grade, target, balance and remaining points are server decisions.
// Labels occupy their own columns; leaders always connect to the exact numerical tick.
export function growthChartLayout(milestones: GrowthMilestone[], balance: number | null,
    size: {width: number; heights?: number[]}): GrowthChartLayout {
  const frame = chartFrame(milestones, balance, size.width);
  const positioned = positionLabels(milestones, frame, size.heights || []);
  const bottom = positioned.reduce((value, item, index) => Math.max(value, item.labelY + ((size.heights || [])[index] || DEFAULT_LABEL_HEIGHT)), ZERO);
  frame.height = frame.vertical ? Math.max(frame.endY + LEADER_HEIGHT, bottom + CHART_PADDING) : bottom + FOOTER_PADDING;
  frame.milestones = positioned;
  frame.intervalStart = positioned.filter(item => item.current)[ZERO];
  frame.intervalEnd = positioned.filter(item => item.next)[ZERO];
  return frame;
}

function chartFrame(milestones: GrowthMilestone[], balance: number | null, width: number): GrowthChartLayout {
  const vertical = width < HORIZONTAL_MIN_WIDTH || width / Math.max(UNIT, milestones.length) < LABEL_COLUMN_WIDTH;
  const labelWidth = vertical ? Math.max(COMPACT_LABEL_WIDTH, width - VERTICAL_LABEL_SPACE) : width / Math.max(UNIT, milestones.length) - LABEL_GAP;
  const greatest = milestones.reduce((value, item) => Math.max(value, item.threshold), ZERO);
  const maximum = Math.max(UNIT, greatest, balance == null ? ZERO : balance);
  const start = vertical ? VERTICAL_START : CHART_PADDING;
  const extent = vertical ? Math.max(MIN_VERTICAL_EXTENT, milestones.length * LABEL_COLUMN_WIDTH) : width - HORIZONTAL_PADDING;
  const axis = vertical ? VERTICAL_AXIS : HORIZONTAL_AXIS;
  const cursor = balance == null ? null : start + balance / maximum * extent;
  const cursorX = cursor == null ? null : (vertical ? axis - CURSOR_OFFSET : cursor);
  const cursorY = cursor == null ? null : (vertical ? cursor : axis - CURSOR_OFFSET);
  return {width: width, height: ZERO, vertical: vertical, labelWidth: labelWidth, maximum: maximum,
    startX: vertical ? axis : start, startY: vertical ? start : axis,
    endX: vertical ? axis : start + extent, endY: vertical ? start + extent : axis,
    cursorX: cursorX, cursorY: cursorY, cursorPoints: diamond(cursorX, cursorY), milestones: []};
}

function diamond(x: number, y: number): string {
  return x == null ? EMPTY : x + COMMA + (y - CURSOR_RADIUS) + SPACE + (x + CURSOR_RADIUS) + COMMA + y + SPACE + x + COMMA + (y + CURSOR_RADIUS) + SPACE + (x - CURSOR_RADIUS) + COMMA + y;
}

function positionLabels(milestones: GrowthMilestone[], frame: GrowthChartLayout, heights: number[]): GrowthMilestone[] {
  const vertical = frame.vertical;
  const axis = vertical ? frame.startX : frame.startY;
  const start = vertical ? frame.startY : frame.startX;
  const extent = (vertical ? frame.endY : frame.endX) - start;
  let bottom = ZERO;
  return milestones.map((item, index) => {
    const height = heights[index] || DEFAULT_LABEL_HEIGHT;
    const tick = start + item.threshold / frame.maximum * extent;
    const top = vertical ? Math.max(ZERO, tick - height / PAIR, bottom + (index ? LABEL_GAP : ZERO)) : axis + HORIZONTAL_PADDING;
    const left = vertical ? axis + LABEL_AXIS_OFFSET : index * (frame.labelWidth + LABEL_GAP) + LABEL_INSET;
    bottom = top + height;
    return Object.assign({}, item, {x: vertical ? axis : tick, y: vertical ? tick : axis,
      labelX: left, labelY: top, leaderX: vertical ? left - LABEL_INSET : left + frame.labelWidth / PAIR,
      leaderY: vertical ? top + Math.min(LEADER_HEIGHT, height / PAIR) : top - LABEL_INSET});
  });
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/grade-growth/grade-growth-chart.ts